<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { Editor } from '@tiptap/core';
  import StarterKit from '@tiptap/starter-kit';

  let {
    value = $bindable(''),
    placeholder = 'Write something...',
    minHeight = '300px'
  } = $props();

  let editor: Editor | null = null;
  let editorElement: HTMLDivElement;
  let isActive = $state({
    bold: false,
    italic: false,
    strike: false,
    code: false,
    heading: false,
    bulletList: false,
    orderedList: false,
    blockquote: false,
    codeBlock: false
  });

  onMount(() => {
    editor = new Editor({
      element: editorElement,
      extensions: [
        StarterKit.configure({
          heading: {
            levels: [1, 2, 3, 4, 5, 6]
          }
        })
      ],
      content: value,
      editorProps: {
        attributes: {
          class: 'prose prose-sm max-w-none focus:outline-none p-4 text-gray-800',
          style: `min-height: ${minHeight};`
        }
      },
      onTransaction: () => {
        // Force reactivity
        if (editor) {
          updateActiveStates();
        }
      },
      onUpdate: ({ editor }) => {
        value = editor.getHTML();
      }
    });

    updateActiveStates();
  });

  onDestroy(() => {
    if (editor) {
      editor.destroy();
    }
  });

  function updateActiveStates() {
    if (!editor) return;

    isActive = {
      bold: editor.isActive('bold'),
      italic: editor.isActive('italic'),
      strike: editor.isActive('strike'),
      code: editor.isActive('code'),
      heading: editor.isActive('heading'),
      bulletList: editor.isActive('bulletList'),
      orderedList: editor.isActive('orderedList'),
      blockquote: editor.isActive('blockquote'),
      codeBlock: editor.isActive('codeBlock')
    };
  }

  function toggleBold() {
    editor?.chain().focus().toggleBold().run();
  }

  function toggleItalic() {
    editor?.chain().focus().toggleItalic().run();
  }

  function toggleStrike() {
    editor?.chain().focus().toggleStrike().run();
  }

  function toggleCode() {
    editor?.chain().focus().toggleCode().run();
  }

  function toggleHeading(level: 1 | 2 | 3) {
    editor?.chain().focus().toggleHeading({ level }).run();
  }

  function toggleBulletList() {
    editor?.chain().focus().toggleBulletList().run();
  }

  function toggleOrderedList() {
    editor?.chain().focus().toggleOrderedList().run();
  }

  function toggleBlockquote() {
    editor?.chain().focus().toggleBlockquote().run();
  }

  function toggleCodeBlock() {
    editor?.chain().focus().toggleCodeBlock().run();
  }

  function insertHorizontalRule() {
    editor?.chain().focus().setHorizontalRule().run();
  }

  function undo() {
    editor?.chain().focus().undo().run();
  }

  function redo() {
    editor?.chain().focus().redo().run();
  }
</script>

<div class="border border-gray-300 rounded-md bg-white shadow-sm overflow-hidden">
  <!-- Toolbar -->
  <div class="border-b border-gray-200 bg-gray-50 p-2 flex items-center gap-1 flex-wrap">
    <!-- Text Formatting -->
    <button
      onclick={toggleBold}
      class="p-2 rounded hover:bg-gray-200 transition-colors {isActive.bold ? 'bg-gray-300 text-gray-900' : 'text-gray-600'}"
      title="Bold (Ctrl+B)"
      type="button"
    >
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 4h8a4 4 0 014 4 4 4 0 01-4 4H6z"/>
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 12h9a4 4 0 014 4 4 4 0 01-4 4H6z"/>
      </svg>
    </button>

    <button
      onclick={toggleItalic}
      class="p-2 rounded hover:bg-gray-200 transition-colors {isActive.italic ? 'bg-gray-300 text-gray-900' : 'text-gray-600'}"
      title="Italic (Ctrl+I)"
      type="button"
    >
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 4l4 16m-2-8h8"/>
      </svg>
    </button>

    <button
      onclick={toggleStrike}
      class="p-2 rounded hover:bg-gray-200 transition-colors {isActive.strike ? 'bg-gray-300 text-gray-900' : 'text-gray-600'}"
      title="Strikethrough"
      type="button"
    >
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12h18M9 5h6m-3 0v14"/>
      </svg>
    </button>

    <button
      onclick={toggleCode}
      class="p-2 rounded hover:bg-gray-200 transition-colors {isActive.code ? 'bg-gray-300 text-gray-900' : 'text-gray-600'}"
      title="Inline Code"
      type="button"
    >
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"/>
      </svg>
    </button>

    <div class="w-px h-6 bg-gray-300 mx-1"></div>

    <!-- Headings -->
    <button
      onclick={() => toggleHeading(1)}
      class="px-2 py-1 rounded hover:bg-gray-200 transition-colors {editor?.isActive('heading', { level: 1 }) ? 'bg-gray-300 text-gray-900' : 'text-gray-600'} font-semibold text-sm"
      title="Heading 1"
      type="button"
    >
      H1
    </button>

    <button
      onclick={() => toggleHeading(2)}
      class="px-2 py-1 rounded hover:bg-gray-200 transition-colors {editor?.isActive('heading', { level: 2 }) ? 'bg-gray-300 text-gray-900' : 'text-gray-600'} font-semibold text-sm"
      title="Heading 2"
      type="button"
    >
      H2
    </button>

    <button
      onclick={() => toggleHeading(3)}
      class="px-2 py-1 rounded hover:bg-gray-200 transition-colors {editor?.isActive('heading', { level: 3 }) ? 'bg-gray-300 text-gray-900' : 'text-gray-600'} font-semibold text-sm"
      title="Heading 3"
      type="button"
    >
      H3
    </button>

    <div class="w-px h-6 bg-gray-300 mx-1"></div>

    <!-- Lists -->
    <button
      onclick={toggleBulletList}
      class="p-2 rounded hover:bg-gray-200 transition-colors {isActive.bulletList ? 'bg-gray-300 text-gray-900' : 'text-gray-600'}"
      title="Bullet List"
      type="button"
    >
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
        <circle cx="4" cy="6" r="1" fill="currentColor"/>
        <circle cx="4" cy="12" r="1" fill="currentColor"/>
        <circle cx="4" cy="18" r="1" fill="currentColor"/>
      </svg>
    </button>

    <button
      onclick={toggleOrderedList}
      class="p-2 rounded hover:bg-gray-200 transition-colors {isActive.orderedList ? 'bg-gray-300 text-gray-900' : 'text-gray-600'}"
      title="Numbered List"
      type="button"
    >
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 6h16M7 12h16M7 18h16M3 6v0M3 12v0M3 18v0"/>
      </svg>
    </button>

    <div class="w-px h-6 bg-gray-300 mx-1"></div>

    <!-- Blockquote & Code Block -->
    <button
      onclick={toggleBlockquote}
      class="p-2 rounded hover:bg-gray-200 transition-colors {isActive.blockquote ? 'bg-gray-300 text-gray-900' : 'text-gray-600'}"
      title="Blockquote"
      type="button"
    >
      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
        <path d="M6 17h3l2-4V7H5v6h3zm8 0h3l2-4V7h-6v6h3z"/>
      </svg>
    </button>

    <button
      onclick={toggleCodeBlock}
      class="p-2 rounded hover:bg-gray-200 transition-colors {isActive.codeBlock ? 'bg-gray-300 text-gray-900' : 'text-gray-600'}"
      title="Code Block"
      type="button"
    >
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
      </svg>
    </button>

    <button
      onclick={insertHorizontalRule}
      class="p-2 rounded hover:bg-gray-200 transition-colors text-gray-600"
      title="Horizontal Rule"
      type="button"
    >
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4"/>
      </svg>
    </button>

    <div class="w-px h-6 bg-gray-300 mx-1"></div>

    <!-- Undo/Redo -->
    <button
      onclick={undo}
      class="p-2 rounded hover:bg-gray-200 transition-colors text-gray-600"
      title="Undo (Ctrl+Z)"
      type="button"
    >
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/>
      </svg>
    </button>

    <button
      onclick={redo}
      class="p-2 rounded hover:bg-gray-200 transition-colors text-gray-600"
      title="Redo (Ctrl+Y)"
      type="button"
    >
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 10H11a8 8 0 00-8 8v2m18-10l-6 6m6-6l-6-6"/>
      </svg>
    </button>
  </div>

  <!-- Editor -->
  <div bind:this={editorElement} class="prose-editor"></div>

  <!-- Footer hint -->
  <div class="bg-gray-50 px-3 py-2 border-t border-gray-200 text-xs text-gray-500 flex justify-between items-center">
    <span>Rich text editor • Supports formatting, lists, and code blocks</span>
  </div>
</div>

<style>
  :global(.prose-editor) {
    background: white;
  }

  :global(.prose-editor .ProseMirror) {
    min-height: 300px;
    padding: 1rem;
    outline: none;
  }

  :global(.prose-editor .ProseMirror:focus) {
    outline: none;
  }

  :global(.prose-editor h1) {
    font-size: 2em;
    font-weight: bold;
    margin-top: 0.67em;
    margin-bottom: 0.67em;
  }

  :global(.prose-editor h2) {
    font-size: 1.5em;
    font-weight: bold;
    margin-top: 0.75em;
    margin-bottom: 0.75em;
  }

  :global(.prose-editor h3) {
    font-size: 1.17em;
    font-weight: bold;
    margin-top: 0.83em;
    margin-bottom: 0.83em;
  }

  :global(.prose-editor p) {
    margin-top: 1em;
    margin-bottom: 1em;
  }

  :global(.prose-editor ul, .prose-editor ol) {
    padding-left: 2em;
    margin-top: 1em;
    margin-bottom: 1em;
  }

  :global(.prose-editor ul) {
    list-style-type: disc;
  }

  :global(.prose-editor ol) {
    list-style-type: decimal;
  }

  :global(.prose-editor code) {
    background-color: #f3f4f6;
    padding: 0.2em 0.4em;
    border-radius: 3px;
    font-size: 0.9em;
    font-family: 'Courier New', monospace;
  }

  :global(.prose-editor pre) {
    background-color: #1e293b;
    color: #e2e8f0;
    padding: 1em;
    border-radius: 0.375rem;
    overflow-x: auto;
    margin-top: 1em;
    margin-bottom: 1em;
  }

  :global(.prose-editor pre code) {
    background-color: transparent;
    padding: 0;
    color: inherit;
  }

  :global(.prose-editor blockquote) {
    border-left: 3px solid #d1d5db;
    padding-left: 1em;
    color: #6b7280;
    font-style: italic;
    margin-top: 1em;
    margin-bottom: 1em;
  }

  :global(.prose-editor hr) {
    border: none;
    border-top: 2px solid #e5e7eb;
    margin-top: 2em;
    margin-bottom: 2em;
  }

  :global(.prose-editor strong) {
    font-weight: bold;
  }

  :global(.prose-editor em) {
    font-style: italic;
  }

  :global(.prose-editor s) {
    text-decoration: line-through;
  }

  :global(.prose-editor .ProseMirror p.is-editor-empty:first-child::before) {
    content: attr(data-placeholder);
    float: left;
    color: #9ca3af;
    pointer-events: none;
    height: 0;
  }
</style>
